import React, { useEffect, useMemo, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button } from "@pd/pd-ui-kit";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import AdditionFieldResolver from "~/pages/IndicatorReports/AdditionFieldResolver";
import GroupSelectResolver from "~/pages/IndicatorReports/GroupSelectResolver/GroupSelectResolver";
import { indicatorReportConfigFormSchema } from "~/components/Forms/IndicatorReportsConfigForm/lib";
import { useGetLocationData } from "~/hooks/locationData";
import { TSelectItem } from "~/model/common";
import {
  getIndicatorReportsFilterGroup,
  handleIndicatorReportHash,
  indicatorReportHashGetterSerializer,
  indicatorReportOpenPreview,
  REPORT_FILTER_FIELD_NAMES,
} from "~/pages/IndicatorReports/lib";
import {
  TypeGroupDealerIndicatorValues,
  useGetIndicatorReportHashLazyQuery,
} from "~/schemas/generated/graphql";
import { useNotify } from "~/shared/hooks/useNotify";
import { Field } from "~/shared/ui/Field";

import s from "./s.less";

export type TIndicatorReportsConfigForm = {
  groupType: TSelectItem<TypeGroupDealerIndicatorValues>;
  DealerIds: TSelectItem<string>[];
  HoldingIds: TSelectItem<string>[];
  RegMenIds: TSelectItem<string>[];
  HoldingsIds: TSelectItem<string>[];
  Country: TSelectItem<string>[];
  FO: TSelectItem<string>[];
  Region: TSelectItem<string>[];
  City: TSelectItem<string>[];
  dateBegin: string;
  dateEnd: string;
  quarter: TSelectItem<number>;
  year: TSelectItem<number>;
};

const IndicatorReportsConfigForm = () => {
  const { t } = useTranslation();
  const { currentId } = useGetLocationData();
  const [groupTypeState, setGroupTypeState] =
    useState<TypeGroupDealerIndicatorValues>(
      TypeGroupDealerIndicatorValues.Dealer,
    );

  const groupOptions = useMemo(() => getIndicatorReportsFilterGroup(t), [t]);

  const { control, handleSubmit, watch, setValue, errors } =
    useForm<TIndicatorReportsConfigForm>({
      defaultValues: {
        groupType: groupOptions[0],
        DealerIds: [],
        RegMenIds: [],
        City: [],
        Region: [],
        FO: [],
      },
      resolver: yupResolver(indicatorReportConfigFormSchema(t, groupTypeState)),
    });

  const groupType = watch(
    "groupType",
  ) as TSelectItem<TypeGroupDealerIndicatorValues>;

  const { notifyReport } = useNotify();

  // Get file hash
  const [getReportHash, getReportHashState] =
    useGetIndicatorReportHashLazyQuery({
      fetchPolicy: "no-cache",
    });

  const getReportByFile = (hash: string) => notifyReport(hash);

  const onSubmitDownload = (data: TIndicatorReportsConfigForm) =>
    getReportHash({
      variables: {
        ReportId: currentId,
        group: groupType.value,
        ...indicatorReportHashGetterSerializer(data),
      },
    });

  const onSubmitPreview = (data: TIndicatorReportsConfigForm) =>
    indicatorReportOpenPreview(data, currentId);

  useEffect(() => {
    setGroupTypeState(groupType.value);
  }, [groupType]);

  useEffect(() => {
    if (groupType?.value)
      Object.values(REPORT_FILTER_FIELD_NAMES).forEach((fieldName) =>
        setValue(fieldName, []),
      );
  }, [groupType]);

  useEffect(() => {
    if (getReportHashState.data && !getReportHashState.loading) {
      const hash = handleIndicatorReportHash(getReportHashState);
      if (handleIndicatorReportHash(getReportHashState)) getReportByFile(hash);
    }
  }, [getReportHashState.loading]);

  return (
    <form className={s.container} onSubmit={(e) => e.preventDefault()}>
      <Field
        type="select"
        name="groupType"
        control={control}
        label={t("reports.grouping")}
        options={groupOptions}
        labelInValue
      />
      {groupType && (
        <GroupSelectResolver
          control={control}
          selectedGroup={groupType.value}
        />
      )}
      <AdditionFieldResolver
        groupType={groupType.value}
        control={control}
        errors={errors}
      />
      <div className={s.controlContainer}>
        <Button
          size={"large"}
          color={"secondary"}
          htmlType="button"
          fontSize={"14"}
          disabled={getReportHashState.loading}
          onClick={handleSubmit(onSubmitPreview)}
        >
          {t("reports.preview")}
        </Button>
        <Button
          htmlType="button"
          size={"large"}
          loading={getReportHashState.loading}
          fontSize={"14"}
          onClick={handleSubmit(onSubmitDownload)}
        >
          {t("reports.download")}
        </Button>
      </div>
    </form>
  );
};

export default IndicatorReportsConfigForm;
